import React, { forwardRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { createUseStyles } from 'react-jss'
import theme from '../../constants/theme'
import { responsiveDinamicStyles } from '../../utils/styles'
import mediaQueries from '../../constants/mediaQueries'

import './rf-contentgrid.scss'

const COLORS = { ...theme['colors'], transparent: 'transparent' }

/**
 * Return the hex color value correspondent to the given string name.
 * @param {string} colorName - The name of the color, one from the color variables list.
 * @return {string } - The hex value of the color.
 */
function colorVarConversion(colorName) {
	if (typeof colorName !== 'string') {
		return null
	}

	const colorPairs = new Map(Object.entries(COLORS))
	if (colorPairs.has(colorName)) {
		return COLORS[colorName]
	}

	return colorName
}

function generateDynamicStyles(props) {
	const styleProps = {
		gap: props.gap,
		padding: props.padding
	}

	let styles = {}
	Object.entries(styleProps).forEach(([propKey, propValue]) => {
		// if prop is not "undefined" or "null"
		if (propValue !== null && typeof propValue !== 'undefined') {
			// if props is unique value,
			if (typeof propValue === 'number') {
				if (propKey === 'padding') {
					styles = {
						...styles,
						[`${propKey}`]: propValue + 'px'
					}
				} else if (propKey === 'gap') {
					styles = {
						...styles,
						gridGap: propValue + 'px'
					}
				}
			}
			// if prop is an object {sm: 5, md....}, generate css rule for each one
			else if (typeof propValue === 'object') {
				Object.keys(propValue).forEach((mq) => {
					if (mediaQueries[mq]) {
						let cssValue = {}

						if (propKey === 'padding') {
							cssValue = { [`${propKey}`]: propValue[mq] + 'px' }
						}

						if (propKey === 'gap') {
							cssValue = { gridGap: propValue[mq] + 'px' }
						}
						const cssResult = responsiveDinamicStyles(mq, { ...cssValue })
						// medaiQueries are objects within the styles object
						// we need to track their key to avoid overwrite them
						if ('mediaQkey' in cssResult) {
							styles[cssResult['mediaQkey']] = {
								...styles[cssResult['mediaQkey']],
								...cssResult['stylesVal']
							}
						} else {
							styles = {
								...styles,
								...cssResult
							}
						}
					}
				})
			}
		}
	})
	return styles
}

const squareStyles = {
	container: {
		position: 'relative',
		width: '100%',
		'&:after': {
			content: '""',
			display: 'block',
			paddingBottom: '100%'
		}
	},
	responsiveSquareGrid: {
		position: 'absolute',
		width: '100%',
		height: '100%'
	}
}

const styles = {
	// Dynamic CSS
	styles: (props) => ({
		...generateDynamicStyles(props),
		backgroundColor: colorVarConversion(props.bgColor)
	}),
	...squareStyles
}

const useStyles = createUseStyles(styles, { name: 'Vb' })

const ContentGrid = forwardRef((props, ref) => {
	const {
		children,
		className,
		cols = 2,
		component: Component = 'ul',
		rowHeight = 'auto',
		gap = 4,
		style: styleProp = {},
		bgColor = 'transparent',
		padding = 0,
		collapse = false,
		colMinWidth = null,
		...other
	} = props

	const gridFeatures = { rowHeight, gap }
	const classes = useStyles(props)

	let gridStyle
	if (rowHeight === 'square') {
		gridStyle = { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, ...styleProp }
	} else {
		gridStyle =
			colMinWidth && collapse
				? { gridTemplateColumns: `repeat(auto-fit, minmax(${colMinWidth}px, 1fr))`, ...styleProp }
				: { gridTemplateColumns: `repeat(${cols}, 1fr)`, ...styleProp }
	}

	const ContentGridComponent = useMemo(() => {
		return (
			<Component
				ref={ref}
				className={clsx(
					'rf-contentgrid',
					className,
					classes.styles,
					rowHeight === 'square' ? classes.responsiveSquareGrid : null
				)}
				style={gridStyle}
				{...other}
			>
				{React.Children.map(children, (child) =>
					React.cloneElement(child, { ...gridFeatures })
				)}
			</Component>
		);
	}, [children, gridFeatures, gridStyle, classes, className, other, ref, rowHeight]);

	return rowHeight === 'square' ? (
		<div className={classes.container}>
			{ContentGridComponent}
		</div>
	) : (ContentGridComponent
	)
})

ContentGrid.displayName = 'ContentGrid'

export default ContentGrid

const isColor = (props, propName, componentName) => {
	const value = props[propName]

	if (typeof value === 'undefined' || typeof value === 'string') {
		return null
	}

	return new Error(
		`${propName} in ${componentName} is not a valid value. Valid values (string): color name, color hex value, color rgba value, or one of [${Object.keys(
			COLORS
		).join(', ')}]`
	)
}

const isNumber = (props, propName, componentName) => {
	const value = props[propName]

	if (typeof value === 'object') {
		let error = null
		const keys = Object.keys(value)
		keys.every((key) => {
			if (!mediaQueries[key]) {
				error = new Error(
					`${propName} object in ${componentName} has an invalid key. Accepted keys: ${Object.keys(mediaQueries).join(
						', '
					)}`
				)
				return false
			}
			if (typeof value[key] === 'number') {
				return true
			}
			error = new Error(`${propName} in ${componentName} is not a valid value. Valid values: number`)
			return false
		})
		if (error) {
			return error
		}
		return null
	} else if (typeof value === 'number' || typeof value === 'undefined') {
		return null
	}
	return new Error(`${propName} in ${componentName} is not a valid value. Valid values: number or object`)
}

ContentGrid.propTypes = {
	/**
	 * Items that will be in the grid,
	 * designed for take "ContentItem" components.
	 */
	children: PropTypes.node.isRequired,
	/**
	 * Override or extend the styles applied to the component.
	 */
	classes: PropTypes.object,
	/**
	 * @ignore
	 */
	className: PropTypes.string,
	/**
	 * Color of the grid's background,
	 * either name, rgba or hex.
	 * @default "transparent"
	 */
	bgColor: isColor,
	/**
	 * Number of columns.
	 * @default 2
	 */
	cols: PropTypes.number,
	/**
	 * The component used for the root node.
	 * Either a string to use a HTML element or a component.
	 * By default, is an <ul> element.
	 */
	component: PropTypes.elementType,
	/**
	 * The gap between items in px.
	 * @default 4
	 */
	gap: isNumber,
	/**
	 * The padding oro the grid in px.
	 * @default 0
	 */
	padding: isNumber,
	/**
	 * The height of one row in px.
	 * If auto, the grid system will manage the row height depending on its cells' content
	 * If square, the grid container will be a responsive square, an its cells will be equal
	 * @default 'auto'
	 */
	rowHeight: PropTypes.oneOfType([PropTypes.oneOf(['auto', 'square']), PropTypes.number]),
	/**
	 * @ignore
	 */
	style: PropTypes.object
}
